import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import api from '../lib/api';
import { 
    RefreshCw, 
    Trash2, 
    Edit3, 
    Loader2, 
    Sparkles,
    Leaf,
    AlertTriangle,
    Heart,
    PiggyBank,
    Check,
    ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export const IngredientSubstitutionModal = ({ 
    isOpen, 
    onClose, 
    ingredient, 
    recipeId,
    packId,
    onSubstitutionApplied 
}) => {
    const [action, setAction] = useState('replace');
    const [reason, setReason] = useState(null);
    const [newIngredient, setNewIngredient] = useState('');
    const [newQuantity, setNewQuantity] = useState(ingredient?.quantity || '');
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [applying, setApplying] = useState(false);

    const reasons = [
        { value: 'vegan', label: 'Végane/Végé', icon: Leaf, color: 'text-green-600' },
        { value: 'allergy', label: 'Allergie', icon: AlertTriangle, color: 'text-red-600' },
        { value: 'preference', label: 'Préférence', icon: Heart, color: 'text-pink-600' },
        { value: 'budget', label: 'Budget', icon: PiggyBank, color: 'text-yellow-600' },
    ];

    const loadSuggestions = async (selectedReason = null) => {
        setLoadingSuggestions(true);
        try {
            const res = await api.getSubstitutionSuggestions(ingredient.name, selectedReason);
            setSuggestions(res.data.suggestions || []);
        } catch (error) {
            console.error('Error loading suggestions:', error);
            toast.error('Erreur lors du chargement des suggestions');
        } finally {
            setLoadingSuggestions(false);
        }
    };

    const handleReasonSelect = (selectedReason) => {
        setReason(selectedReason);
        loadSuggestions(selectedReason);
    };

    const handleSuggestionSelect = (suggestion) => {
        setNewIngredient(suggestion.name);
        // Adjust quantity based on ratio if needed
        if (suggestion.ratio && suggestion.ratio !== 'même quantité') {
            setNewQuantity(`${ingredient.quantity} (${suggestion.ratio})`);
        }
    };

    const handleApply = async () => {
        if (action === 'replace' && !newIngredient.trim()) {
            toast.error('Veuillez entrer un ingrédient de remplacement');
            return;
        }
        
        if (action === 'modify_quantity' && !newQuantity.trim()) {
            toast.error('Veuillez entrer une nouvelle quantité');
            return;
        }

        setApplying(true);
        try {
            await api.substituteIngredient(packId, {
                recipe_id: recipeId,
                original_ingredient: ingredient.name,
                action: action,
                new_ingredient: action === 'replace' ? newIngredient : null,
                new_quantity: (action === 'replace' || action === 'modify_quantity') ? newQuantity : null
            });

            const messages = {
                replace: `"${ingredient.name}" remplacé par "${newIngredient}"`,
                modify_quantity: `Quantité de "${ingredient.name}" modifiée`,
                exclude: `"${ingredient.name}" exclu de votre liste`
            };
            
            toast.success(messages[action]);
            onSubstitutionApplied?.();
            onClose();
        } catch (error) {
            console.error('Error applying substitution:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        } finally {
            setApplying(false);
        }
    };

    const resetState = () => {
        setAction('replace');
        setReason(null);
        setNewIngredient('');
        setNewQuantity(ingredient?.quantity || '');
        setSuggestions([]);
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetState(); onClose(); } }}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Edit3 className="w-5 h-5 text-primary" />
                        Modifier l'ingrédient
                    </DialogTitle>
                    <DialogDescription>
                        <span className="font-semibold text-foreground">{ingredient?.name}</span>
                        <span className="text-muted-foreground ml-2">({ingredient?.quantity})</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-4">
                    {/* Action Selection */}
                    <div className="space-y-3">
                        <Label className="text-sm font-semibold">Que voulez-vous faire ?</Label>
                        <RadioGroup value={action} onValueChange={setAction} className="grid grid-cols-1 gap-2">
                            <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${action === 'replace' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                                <RadioGroupItem value="replace" id="replace" />
                                <RefreshCw className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">Remplacer par un autre</span>
                            </label>
                            <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${action === 'modify_quantity' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                                <RadioGroupItem value="modify_quantity" id="modify_quantity" />
                                <Edit3 className="w-4 h-4 text-orange-500" />
                                <span className="font-medium">Modifier la quantité</span>
                            </label>
                            <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${action === 'exclude' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                                <RadioGroupItem value="exclude" id="exclude" />
                                <Trash2 className="w-4 h-4 text-red-500" />
                                <span className="font-medium">Exclure de ma liste</span>
                            </label>
                        </RadioGroup>
                    </div>

                    {/* Replace Options */}
                    {action === 'replace' && (
                        <div className="space-y-4">
                            {/* Reason Selection */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Pourquoi ? (pour de meilleures suggestions)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {reasons.map((r) => (
                                        <Button
                                            key={r.value}
                                            type="button"
                                            variant={reason === r.value ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => handleReasonSelect(r.value)}
                                            className="rounded-full"
                                        >
                                            <r.icon className={`w-3 h-3 mr-1 ${reason === r.value ? 'text-white' : r.color}`} />
                                            {r.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* AI Suggestions */}
                            {loadingSuggestions ? (
                                <div className="flex items-center justify-center py-6 bg-gray-50 rounded-xl">
                                    <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
                                    <span className="text-sm text-muted-foreground">Génération des suggestions...</span>
                                </div>
                            ) : suggestions.length > 0 ? (
                                <div className="space-y-2">
                                    <Label className="text-sm font-semibold flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-yellow-500" />
                                        Suggestions IA
                                    </Label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {suggestions.map((s, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => handleSuggestionSelect(s)}
                                                className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                    newIngredient === s.name 
                                                        ? 'border-primary bg-primary/5' 
                                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold text-sm">{s.name}</span>
                                                    {newIngredient === s.name && <Check className="w-4 h-4 text-primary" />}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    <span className="text-primary font-medium">{s.ratio}</span>
                                                    <span className="mx-1">•</span>
                                                    {s.note}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : reason && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => loadSuggestions(reason)}
                                    className="w-full"
                                >
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Obtenir des suggestions IA
                                </Button>
                            )}

                            {/* Manual Input */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold">Ou entrez manuellement</Label>
                                <Input
                                    value={newIngredient}
                                    onChange={(e) => setNewIngredient(e.target.value)}
                                    placeholder="Nouvel ingrédient..."
                                    className="rounded-xl"
                                />
                            </div>

                            {/* New Quantity */}
                            {newIngredient && (
                                <div className="space-y-2">
                                    <Label className="text-sm font-semibold">Quantité</Label>
                                    <Input
                                        value={newQuantity}
                                        onChange={(e) => setNewQuantity(e.target.value)}
                                        placeholder="Quantité..."
                                        className="rounded-xl"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Modify Quantity */}
                    {action === 'modify_quantity' && (
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold">Nouvelle quantité</Label>
                            <Input
                                value={newQuantity}
                                onChange={(e) => setNewQuantity(e.target.value)}
                                placeholder="Ex: 2 tasses, 500g..."
                                className="rounded-xl"
                            />
                            <p className="text-xs text-muted-foreground">
                                Quantité actuelle : {ingredient?.quantity}
                            </p>
                        </div>
                    )}

                    {/* Exclude Confirmation */}
                    {action === 'exclude' && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <p className="text-sm text-red-700">
                                <strong>"{ingredient?.name}"</strong> sera retiré de votre liste d'épicerie pour ce pack.
                            </p>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => { resetState(); onClose(); }}
                        className="flex-1"
                        disabled={applying}
                    >
                        Annuler
                    </Button>
                    <Button
                        type="button"
                        onClick={handleApply}
                        disabled={applying}
                        className="flex-1 bg-primary"
                    >
                        {applying ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <ArrowRight className="w-4 h-4 mr-2" />
                        )}
                        Appliquer
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default IngredientSubstitutionModal;
